<template>
  <nav class="vendor-navbar">
    <div class="vendor-navbar__inner">
      <RouterLink class="vendor-navbar__brand" to="/dashboard">
        <img src="@/assets/logo.png" alt="Invi8" class="vendor-navbar__logo">
      </RouterLink>

      <div class="vendor-navbar__links">
        <RouterLink class="vendor-navbar__link" to="/dashboard">Overview</RouterLink>
        <RouterLink class="vendor-navbar__link" to="/dashboard/enquiries">Enquiries</RouterLink>
        <RouterLink class="vendor-navbar__link" to="/dashboard/settings">Settings</RouterLink>
      </div>

      <div class="vendor-navbar__actions">
        <button type="button" class="vendor-navbar__profile" @click="menuOpen = !menuOpen">
          <span class="vendor-navbar__avatar">
            <i class="ri-store-2-line"></i>
          </span>
          <span class="vendor-navbar__name">{{ displayName }}</span>
          <i class="ri-arrow-down-s-line vendor-navbar__chevron"></i>
        </button>

        <transition name="fade">
          <div v-if="menuOpen" ref="menuRef" class="vendor-navbar__menu">
            <RouterLink class="vendor-navbar__menu-item" to="/dashboard" @click="closeMenu">
              Dashboard
            </RouterLink>
            <RouterLink class="vendor-navbar__menu-item" to="/dashboard/enquiries" @click="closeMenu">
              Enquiries
            </RouterLink>
            <RouterLink class="vendor-navbar__menu-item" to="/dashboard/settings" @click="closeMenu">
              Settings
            </RouterLink>
            <button type="button" class="vendor-navbar__menu-item vendor-navbar__menu-item--danger" @click="handleLogout">
              Logout
            </button>
          </div>
        </transition>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import Swal from 'sweetalert2'
import { clearAuth, getAuthUser } from '@/services/auth'

const router = useRouter()
const menuOpen = ref(false)
const menuRef = ref(null)

const authUser = computed(() => getAuthUser() || {})
const displayName = computed(() =>
  authUser.value?.business_name ||
  authUser.value?.s_name ||
  authUser.value?.name ||
  authUser.value?.s_username ||
  'Vendor'
)

const closeMenu = () => {
  menuOpen.value = false
}

const handleClickOutside = (event) => {
  if (!menuRef.value) return
  if (!menuRef.value.parentElement?.contains(event.target)) {
    closeMenu()
  }
}

const handleLogout = () => {
  Swal.fire({
    title: 'Logout?',
    text: 'Your vendor session will be cleared.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Logout',
    cancelButtonText: 'Cancel'
  }).then((result) => {
    if (!result.isConfirmed) return
    clearAuth()
    closeMenu()
    router.push('/login?role=vendor')
  })
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.vendor-navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1040;
  border-bottom: 1px solid rgba(229, 216, 204, 0.9);
  background: rgba(255, 250, 245, 0.94);
  backdrop-filter: blur(14px);
  box-shadow: 0 16px 40px rgba(79, 55, 36, 0.08);
}

.vendor-navbar__inner {
  margin: 0 auto;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  max-width: 1280px;
  align-items: center;
  gap: 20px;
  padding: 14px 24px;
}

.vendor-navbar__brand {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  color: #1f1208;
  font-size: 0.96rem;
  font-weight: 800;
  text-decoration: none;
}

.vendor-navbar__logo {
  height: 38px;
  width: auto;
}

.vendor-navbar__links {
  display: flex;
  min-width: 0;
  flex: 1;
  justify-content: center;
  gap: 8px;
  overflow-x: auto;
  scrollbar-width: none;
}

.vendor-navbar__links::-webkit-scrollbar {
  display: none;
}

.vendor-navbar__link {
  flex: 0 0 auto;
  border-radius: 999px;
  padding: 10px 16px;
  color: #6f5948;
  font-size: 0.92rem;
  font-weight: 700;
  text-decoration: none;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.vendor-navbar__link:hover,
.vendor-navbar__link.router-link-active {
  background: #fff1e4;
  color: #d56b32;
}

.vendor-navbar__actions {
  position: relative;
  justify-self: end;
  min-width: 0;
}

.vendor-navbar__profile {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #eadfd4;
  border-radius: 999px;
  background: #fff;
  padding: 6px 10px 6px 6px;
  color: #1f1208;
}

.vendor-navbar__avatar {
  display: inline-flex;
  height: 36px;
  width: 36px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #fff3e6;
  color: #d56b32;
  font-size: 1rem;
}

.vendor-navbar__name {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.9rem;
  font-weight: 700;
}

.vendor-navbar__chevron {
  color: #8e725c;
  font-size: 1rem;
}

.vendor-navbar__menu {
  position: absolute;
  right: 0;
  top: calc(100% + 10px);
  min-width: 180px;
  max-width: calc(100vw - 24px);
  overflow: hidden;
  border: 1px solid #eadfd4;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 18px 38px rgba(79, 55, 36, 0.14);
}

.vendor-navbar__menu-item {
  display: block;
  width: 100%;
  border: 0;
  background: transparent;
  padding: 12px 16px;
  color: #1f1208;
  font-size: 0.9rem;
  font-weight: 600;
  text-align: left;
  text-decoration: none;
}

.vendor-navbar__menu-item:hover {
  background: #fff7ef;
}

.vendor-navbar__menu-item--danger {
  color: #dc2626;
}

@media (max-width: 991px) {
  .vendor-navbar__inner {
    width: 100%;
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      "brand actions"
      "links links";
    gap: 10px 12px;
    padding: 10px 12px;
  }

  .vendor-navbar__brand {
    grid-area: brand;
    min-width: 0;
  }

  .vendor-navbar__logo {
    height: 32px;
  }

  .vendor-navbar__links {
    grid-area: links;
    width: 100%;
    justify-content: flex-start;
    gap: 6px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 2px;
  }

  .vendor-navbar__link {
    padding: 8px 12px;
    font-size: 0.86rem;
  }

  .vendor-navbar__actions {
    grid-area: actions;
  }

  .vendor-navbar__profile {
    max-width: calc(100vw - 120px);
    gap: 8px;
    padding: 5px 9px 5px 5px;
  }

  .vendor-navbar__avatar {
    height: 32px;
    width: 32px;
  }

  .vendor-navbar__name {
    max-width: 128px;
  }
}

@media (max-width: 575px) {
  .vendor-navbar__inner {
    padding-inline: 10px;
  }

  .vendor-navbar__link {
    padding: 7px 10px;
  }

  .vendor-navbar__name {
    display: none;
  }
}
</style>
